
import { Component, Prop, Vue, Watch, Emit } from "vue-property-decorator";
import { Getter, namespace } from "vuex-class";
import vSelect from "vue-select"; // @ref https://sagalbot.github.io/vue-select/
import UrlInputLine from "@/components/UrlInputLine.vue";
import NotificationSetup from "@/components/tasks/notification/NotificationSetup.vue";
import NewTask from "@/models/new-task.model";
import { TaskType, TaskCategory } from "@/store/tasks/types";
import BasicTask from "@/components/tasks/task-types/BasicTask.vue";
import InputTask from "@/components/tasks/task-types/InputTask.vue";
import YesNoTask from "@/components/tasks/task-types/YesNoTask.vue";
import UrlTask from "@/components/tasks/task-types/UrlTask.vue";
import SitRepTask from "@/components/tasks/task-types/SitRepTask.vue";
import CCureTask from "@/components/tasks/task-types/CCureTask.vue";
import AuditTask from "@/components/tasks/task-types/AuditTask.vue";
import ChoicesTaskSetup from "@/components/tasks/choices/ChoicesTaskSetup.vue";
import CallContactSetup from "@/components/tasks/call-contact/CallContactSetup.vue";
import DynamicDataList from "@/components/tasks/task-types/DynamicDataList.vue"
import ControlTask from "@/components/tasks/control/ControlSetup.vue";
import EventOutcomeTask from "@/components/tasks/task-types/EventOutcomeTask.vue";

import { FeaturesList, UserPermissions } from "@/store/types";
import TaskTypeIds from "@/types/sv-data/enums/TaskTypeIds";
import ChangePriorityTask from "./task-types/ChangePriorityTask.vue";
import LabelDropdown from "../labels/LabelDropdown.vue";
import VirtualOperatorSettings from "@/components/tasks/VirtualOperatorSettings.vue";
import VirtualOperatorSettingsModel from "@/types/sv-data/VirtualOperatorSettings";
import { isEqual } from "lodash";
import { LabelTypes } from "@/store/labels/types";

const Tasks = namespace("tasks");

@Component({
	components: {
		"vue-select": vSelect,
		"url-input": UrlInputLine,
		"notification-setup": NotificationSetup,
		"basic-task": BasicTask,
		"input-task": InputTask,
		"yes-no-task": YesNoTask,
		"url-task": UrlTask,
		"sitrep-task": SitRepTask,
		"ccure-task": CCureTask,
		"audit-task": AuditTask,
		"choices-task-setup": ChoicesTaskSetup,
		"call-contact-setup": CallContactSetup,
		"dynamic-data-list": DynamicDataList,
		"control-task": ControlTask,
		"event-outcome-task": EventOutcomeTask,
		"change-priority-task": ChangePriorityTask,
		"label-dropdown": LabelDropdown,
		"virtual-operator-settings": VirtualOperatorSettings
	}
})
export default class EditLibraryTask extends Vue {
	public hasValidationErrors: boolean = false;
	public showErrorAnimation: boolean = false; // @otodo move to app-wide setting
	public taskTypeSelectPlaceholder: any = null;

	public placeholderText = {
		1: "Action text",
		2: "Specify question that will require user input to complete action",
		3: "Specify question that will require user to choose Yes/No to complete action"
	};

	@Tasks.Getter("getEnabledActionTypes") enabledActionTypes: TaskType[];
	@Getter("getFeaturesList") featuresList: FeaturesList;
	@Getter getFeature: (featuresList: string[]) => boolean;
	@Getter getPermissions: UserPermissions;

	private TaskTypeIdEnum: typeof TaskTypeIds = TaskTypeIds;

	@Prop(Array)
	public taskTypes!: TaskType[];

	@Prop(Array)
	public taskCategories!: TaskCategory[];

	@Prop({
		default: () => ({
			taskID: {
				// non-editable
				type: Number,
				required: true
			},
			taskText: {
				type: String,
				required: true
			},
			taskTypeID: {
				type: Number,
				required: false
			},
			taskCategoryID: {
				type: Number,
				required: true
			}
		})
	})
	public readonly libraryTask!: any;

	public taskToRender: string = "basic-task";
	public isTaskValid: boolean = false;

	/**
	 * A clone of the libraryTask passed in from the parent. Changes are applied to the clone when editing.
	 */
	private taskToEdit = {
		taskID: 0,
		taskText: "",
		taskTypeID: 0,
		taskCategoryID: 0,
		taskData: null,
		virtualOperatorSettings: { context: "", isConcluding: false }
	};

	private originalTaskLabels: number[] = [];
	private editedLabels: number[] = [];
	private labelTypeId: number = LabelTypes.ActionPlan;

	// === Watchers === //
	@Watch("libraryTask")
	public onLibraryTaskChanged() {
		this.taskToEdit = { ...this.libraryTask };
		this.taskToEdit.virtualOperatorSettings = this.normalizeVirtualOperatorSettings(this.libraryTask.virtualOperatorSettings);
	}

	// The server stores VirtualOperatorSettings as a JSON string; the editor works with an object.
	private normalizeVirtualOperatorSettings(settings: any): VirtualOperatorSettingsModel {
		if (!settings) {
			return { context: "", isConcluding: false };
		}
		if (typeof settings === "string") {
			try {
				const parsed = JSON.parse(settings);
				return { context: (parsed && parsed.context) || "", isConcluding: !!(parsed && parsed.isConcluding) };
			} catch {
				return { context: "", isConcluding: false };
			}
		}
		return { context: settings.context || "", isConcluding: !!settings.isConcluding };
	}

	@Watch("libraryTask.title")
	public onTitleChanged(title: string) {
		this.hasValidationErrors = !title.length;
	}

	// === Computed Props === //
	public get libraryTaskCategory() {
		return this.taskCategories.filter(
			category =>
				category.taskCategoryID === this.taskToEdit.taskCategoryID
		)[0];
	}

	public set libraryTaskCategory(taskCategory: TaskCategory) {
		this.taskToEdit.taskCategoryID = taskCategory.taskCategoryID || 1;
	}

	public get libraryTaskType() {
		return this.enabledActionTypes.filter(
			type => type.taskTypeID === this.taskToEdit.taskTypeID
		)[0];
	}

	public set libraryTaskType(taskType: TaskType) {
		if (taskType === null || taskType === undefined) return;

		this.taskToEdit.taskTypeID = taskType.taskTypeID || 1;

		this.loadCorrectComponent();
	}

	public get isValid() {
		let isValid = this.isTaskValid;

		switch (this.taskToEdit.taskTypeID) {
			case 4: // no validation for dispatch type
				isValid = true;
				break;
			default:
				isValid = this.isTaskValid;
		}

		// taskType defaults to basic
		if (!this.taskToEdit.taskTypeID) {
			this.taskToEdit.taskTypeID = 1;
		}

		return isValid;
	}

	private get isLabelingFeatureEnabled(): boolean {
		return this.getFeature(["Labelling", "ActionPlanSetup"]);
	}

	public get canEditVirtualOperatorActionPlanSettings(): boolean {
		return this.getFeature(["AI", "VirtualOperators", "ActionPlanSettings"]) && !!(this.getPermissions && this.getPermissions.canEditVirtualOperatorActionPlanSettings);
	}

	public get isVirtualOperatorAnswerableTaskType(): boolean {
		const taskTypeID = this.taskToEdit.taskTypeID;
		return taskTypeID === TaskTypeIds.Input
			|| taskTypeID === TaskTypeIds.YesNo
			|| taskTypeID === TaskTypeIds.Choices
			|| taskTypeID === TaskTypeIds.ChangePriority;
	}

	// === Methods === //
	public mounted() {
		this.onLibraryTaskChanged();
		this.loadCorrectComponent();
	}

	public loadCorrectComponent() {
		let task = this.enabledActionTypes.find(
			(taskType: any) =>
				taskType.taskTypeID === this.libraryTask.taskTypeID &&
				taskType.componentFile !== null
		);

		// Avoid property undefined errors if a task was not returned.
		if (task !== undefined) {
			this.taskToRender = task.componentFile;
		}
	}

	// === Events === //
	@Emit("save")
	public save() {
		return this.taskToEdit;
	}

	@Emit("cancel")
	public cancel() {
		return this.taskToEdit.taskID;
	}

	// Called when a task component verifies that it is valid.
	public onTaskValidated(valid: boolean) {
		this.isTaskValid = valid;
	}

	public onSaveClicked() {
		if (this.canSave) {
			this.confirmUrlTaskHasTitle();

			if (this.isLabelingFeatureEnabled) {
				(this.taskToEdit as any).labelIds = this.editedLabels;
			}

			this.save();
		}
	}

	private confirmUrlTaskHasTitle() {
		if (this.taskToEdit.taskTypeID === TaskTypeIds.URL) {
			if (this.taskToEdit.taskText === "") {
				this.taskToEdit.taskText = this.taskToEdit.taskData;
				this.taskToEdit.taskData = null;
			}
		}
	}

	private updateLabels(labels: number[]): void {
		this.editedLabels = labels;
	}

	private initialTaskLabels(labels: number[]): void {
		this.originalTaskLabels = labels;
		this.editedLabels = labels;
	}

	private get hasTaskChanged(): boolean {
		return this.taskToEdit.taskText !== this.libraryTask.taskText
			|| this.taskToEdit.taskTypeID !== this.libraryTask.taskTypeID
			|| this.taskToEdit.taskCategoryID !== this.libraryTask.taskCategoryID
			|| !isEqual(
				this.normalizeVirtualOperatorSettings(this.taskToEdit.virtualOperatorSettings),
				this.normalizeVirtualOperatorSettings(this.libraryTask.virtualOperatorSettings))
			|| !isEqual(this.taskToEdit.taskData, this.libraryTask.taskData);
	}

	private get hasLabelsChanged(): boolean {
		return !isEqual(this.originalTaskLabels, this.editedLabels);
	}

	private get canSave(): boolean {
		var status = this.isValid && this.hasTaskChanged;

		if (this.isLabelingFeatureEnabled && this.hasLabelsChanged) {
			const isNew = this.taskToEdit.taskID === 0;
			// Button enabled conditions:
			// If task is being created, true when data is valid and has changed
			// If task is being edited, true when task is valid and has changed or when task has not been changed but labels have changed
			status = isNew ? status : !this.hasTaskChanged || status;

		}

		return status;
	}
}
